/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormMethods, notify } from '@wyz/ui';
import {
  CustomersBrandsForm,
  CustomersConfigurationForm,
  CustomersQuoteExpressForm,
  CustomersContactForm,
  CustomersCriteriaForm,
  CustomersDeliveryBillingForm,
  CustomersGeneralForm,
  CustomersQuoteBrandsForm,
  CustomersRedistributionBrandsForm,
  CustomersSettingTemplateForm,
  CustomersSuppliersForm,
  InternalStocksForm,
} from '~/connect/components';

import { zodResolver } from '@hookform/resolvers/zod';
import { IErrorApiResponse, IValidationError } from '@wyz/types';
import { useOnUpdate } from '@wyz/utils';
import { pick } from 'lodash-es';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useManageCustomers } from '~/common/api';
import { useApplicationContext, useI18n, Trans } from '~/common/context';
import { FEATURES, ROLES, Validations } from '~/common/helpers';
import { useAccessControl, useCommonErrorTranslation } from '~/common/hooks';
import { CUSTOMER_LEVEL } from '@wyz/types';
import { CommonErrorCodesEnumType, ICustomer } from '~/common/types';
import { CustomerUtils } from '~/connect/helpers';
import {
  brandsFormSchema,
  configurationFormSchema,
  quoteInformationFormSchema,
  contactFormSchema,
  customerSettingTemplateFormSchema,
  deliveryAndBillingFormSchema,
  entityCriteriaFormSchema,
  generalFormValidationSchema,
  internalStocksFormSchema,
  quoteBrandsFormSchema,
  redistributionBrandsFormSchema,
  suppliersFormSchema,
} from '~/connect/schemas';
import {
  CustomerFormStep,
  ICustomerBrandsFrom,
  ICustomerQuoteInformationForm,
  ICustomerConfigurationForm,
  ICustomerContactForm,
  ICustomerCriteriaForm,
  ICustomerDeliveryInformationForm,
  ICustomerForm,
  ICustomerGeneralForm,
  ICustomerInternalStocksForm,
  ICustomerQuoteBrandsForm,
  ICustomerRedistributionBrandsForm,
  ICustomerSettingTemplateFrom,
  ICustomerSuppliersForm,
  CustomerCriterion,
} from '~/connect/types';
import { RESERVED_CRITERIA_CODE } from '~/common/schemas';

export type CustomerFormProps = {
  customer?: ICustomer;
  mode: 'UPDATE' | 'CREATE';
  forms: ReturnType<typeof useCustomerForm>['forms'];
  internalCriteria: ReturnType<typeof useCustomerForm>['internalCriteria'];
  isWriting: boolean;
};

type ICustomersFormsResult = {
  forms: [
    {
      methods: FormMethods<ICustomerGeneralForm>;
      component: React.FC<CustomerFormProps>;
      stepKey: CustomerFormStep;
    },
    {
      methods: FormMethods<ICustomerCriteriaForm>;
      component: React.FC<CustomerFormProps>;
      stepKey: CustomerFormStep;
    },
    {
      methods: FormMethods<ICustomerQuoteInformationForm>;
      component: React.FC<CustomerFormProps>;
      stepKey: CustomerFormStep;
    },
    {
      methods: FormMethods<ICustomerContactForm>;
      component: React.FC<CustomerFormProps>;
      stepKey: CustomerFormStep;
    },
    {
      methods: FormMethods<ICustomerDeliveryInformationForm>;
      component: React.FC<CustomerFormProps>;
      stepKey: CustomerFormStep;
    },
    {
      methods: FormMethods<ICustomerConfigurationForm>;
      component: React.FC<CustomerFormProps>;
      stepKey: CustomerFormStep;
    },
    {
      methods: FormMethods<ICustomerSuppliersForm>;
      component: React.FC<CustomerFormProps>;
      stepKey: CustomerFormStep;
    },
    {
      methods: FormMethods<ICustomerBrandsFrom>;
      component: React.FC<CustomerFormProps>;
      stepKey: CustomerFormStep;
    },
    {
      methods: FormMethods<ICustomerQuoteBrandsForm>;
      component: React.FC<CustomerFormProps>;
      stepKey: CustomerFormStep;
    },
    {
      methods: FormMethods<ICustomerRedistributionBrandsForm>;
      component: React.FC<CustomerFormProps>;
      stepKey: CustomerFormStep;
    },
    {
      methods: FormMethods<ICustomerSettingTemplateFrom>;
      component: React.FC<CustomerFormProps>;
      stepKey: CustomerFormStep;
    },
    {
      methods: FormMethods<ICustomerInternalStocksForm>;
      component: React.FC<CustomerFormProps>;
      stepKey: CustomerFormStep;
    },
  ];
  internalCriteria: Array<CustomerCriterion>;
};
type ICustomersFormArgs = {
  defaultValues?: Partial<ICustomerForm>;
  error?: IErrorApiResponse<
    IValidationError<CommonErrorCodesEnumType>,
    CommonErrorCodesEnumType
  > | null;
};

export const useCustomerForm = ({
  defaultValues,
  error,
}: ICustomersFormArgs = {}): ICustomersFormsResult => {
  const { t } = useI18n();
  const { resolveErrorKey } = useCommonErrorTranslation();
  const generalFormMethods = useForm<ICustomerGeneralForm>({
    reValidateMode: 'onChange',
    mode: 'onChange',
    resolver: zodResolver(generalFormValidationSchema),
    defaultValues: {
      // Author's note : The Pick is really critical so we don't overwrite values between steps
      ...pick(defaultValues ?? CustomerUtils.DEFAULT_VALUES, [
        'customerCode',
        'name',
        'commercialName',
        'parent',
        'level',
        'isSupplier',
        'customerGroup',
        'logo',
        'customName',
        'redistributionMaxDelay',
        'locale',
        'legalForm',
        'rcsNumber',
        'vatNumber',
      ]),
    },
  });

  const criteriaFormMethods = useForm<ICustomerCriteriaForm>({
    reValidateMode: 'onChange',
    mode: 'onChange',
    resolver: zodResolver(entityCriteriaFormSchema),
    // Author's note : The Pick is really critical so we don't overwrite values between steps
    defaultValues: pick(defaultValues ?? CustomerUtils.DEFAULT_VALUES, [
      'criteria',
    ]),
  });
  const contactFormMethods = useForm<ICustomerContactForm>({
    reValidateMode: 'onChange',
    mode: 'onChange',
    resolver: zodResolver(contactFormSchema),
    // Author's note : The Pick is really critical so we don't overwrite values between steps
    defaultValues: pick(defaultValues ?? CustomerUtils.DEFAULT_VALUES, [
      'contacts',
    ]),
  });
  const deliveryAndBillingFormMethods =
    useForm<ICustomerDeliveryInformationForm>({
      reValidateMode: 'onChange',
      mode: 'onChange',
      resolver: zodResolver(deliveryAndBillingFormSchema),
      // Author's note : The Pick is really critical so we don't overwrite values between steps
      defaultValues: pick(defaultValues ?? CustomerUtils.DEFAULT_VALUES, [
        'deliveryInformation',
        'billingInformation',
      ]),
    });
  const configurationFormMethods = useForm<ICustomerConfigurationForm>({
    reValidateMode: 'onChange',
    mode: 'onChange',
    resolver: zodResolver(configurationFormSchema),
    // Author's note : The Pick is really critical so we don't overwrite values between steps
    defaultValues: pick(defaultValues ?? CustomerUtils.DEFAULT_VALUES, [
      'configuration',
    ]),
  });

  const suppliersFormMethods = useForm<ICustomerSuppliersForm>({
    reValidateMode: 'onChange',
    mode: 'onChange',
    resolver: zodResolver(suppliersFormSchema),
    // Author's note : The Pick is really critical so we don't overwrite values between steps
    defaultValues: pick(defaultValues ?? CustomerUtils.DEFAULT_VALUES, [
      'supplierConfigurations',
      'supplierTemplate',
    ]),
  });

  const quoteBrandsFormMethods = useForm<ICustomerQuoteBrandsForm>({
    resolver: zodResolver(quoteBrandsFormSchema),
    // Author's note : The Pick is really critical so we don't overwrite values between steps
    defaultValues: {},
  });

  const redistributionBrandsFormMethods =
    useForm<ICustomerRedistributionBrandsForm>({
      resolver: zodResolver(redistributionBrandsFormSchema),
      // Author's note : The Pick is really critical so we don't overwrite values between steps
      defaultValues: {},
    });

  const brandsFormMethods = useForm<ICustomerBrandsFrom>({
    resolver: zodResolver(brandsFormSchema),
    // Author's note : The Pick is really critical so we don't overwrite values between steps
    defaultValues: {},
  });
  const settingTemplateFormMethods = useForm<ICustomerSettingTemplateFrom>({
    resolver: zodResolver(customerSettingTemplateFormSchema),
    // Author's note : The Pick is really critical so we don't overwrite values between steps
    defaultValues: {},
  });
  const internalStocksFormMethods = useForm<ICustomerInternalStocksForm>({
    resolver: zodResolver(internalStocksFormSchema),
    // Author's note : The Pick is really critical so we don't overwrite values between steps
    defaultValues: pick(defaultValues ?? CustomerUtils.DEFAULT_VALUES, [
      'internalStocks',
    ]),
  });
  const quoteExpressFormMethods = useForm<ICustomerQuoteInformationForm>({
    resolver: zodResolver(quoteInformationFormSchema),
    defaultValues: pick(defaultValues ?? CustomerUtils.DEFAULT_VALUES, [
      'quoteInformation',
    ]),
  });

  const STEPS_FORMS: ICustomersFormsResult['forms'] = [
    {
      methods: generalFormMethods,
      component: CustomersGeneralForm,
      stepKey: 'GENERAL_FORM',
    },
    {
      methods: criteriaFormMethods,
      component: CustomersCriteriaForm,
      stepKey: 'CRITERIA_FORM',
    },
    {
      methods: quoteExpressFormMethods,
      component: CustomersQuoteExpressForm,
      stepKey: 'QUOTE_EXPRESS_FORM',
    },
    {
      methods: contactFormMethods,
      component: CustomersContactForm,
      stepKey: 'CONTACT_FORM',
    },
    {
      methods: deliveryAndBillingFormMethods,
      component: CustomersDeliveryBillingForm,
      stepKey: 'DELIVERY_FORM',
    },
    {
      methods: configurationFormMethods,
      component: CustomersConfigurationForm,
      stepKey: 'CONFIGURATION_FORM',
    },
    {
      methods: suppliersFormMethods,
      component: CustomersSuppliersForm,
      stepKey: 'SUPPLIERS_FORM',
    },
    {
      methods: brandsFormMethods,
      component: CustomersBrandsForm,
      stepKey: 'BRANDS_FORM',
    },
    {
      methods: quoteBrandsFormMethods,
      component: CustomersQuoteBrandsForm,
      stepKey: 'QUOTE_BRANDS_FORM',
    },
    {
      methods: redistributionBrandsFormMethods,
      component: CustomersRedistributionBrandsForm,
      stepKey: 'REDISTRIBUTION_BRANDS_FORM',
    },
    {
      methods: settingTemplateFormMethods,
      component: CustomersSettingTemplateForm,
      stepKey: 'SETTING_TEMPLATE_FORM',
    },
    {
      methods: internalStocksFormMethods,
      component: InternalStocksForm,
      stepKey: 'INTERNAL_STOCK_FORM',
    },
  ];

  useOnUpdate(() => {
    if (error?.errors) {
      STEPS_FORMS.forEach((stepForm) => {
        Validations.setFormErrors({
          setError: stepForm.methods.setError as FormMethods<any>['setError'],
          errors: Validations.formatValidationErrors(error.errors, t),
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error?.errors]);

  useOnUpdate(() => {
    if (error?.message) {
      notify.error(<Trans i18nKey={resolveErrorKey(error?.message)} />, {
        id: 'toast-error-customer-form-error',
      });
    }
  }, [error?.message, resolveErrorKey]);
  const watchedLevel = useCustomerFormLevel({
    parent: generalFormMethods.getValues('parent'),
  });
  return {
    forms: STEPS_FORMS,
    internalCriteria: [
      {
        id: RESERVED_CRITERIA_CODE.COUNTRY,
        valueId: deliveryAndBillingFormMethods.getValues(
          'deliveryInformation.country.isoCode',
        ),
      },
      {
        id: RESERVED_CRITERIA_CODE.CUSTOMER,
        valueId: generalFormMethods.getValues('parent.id.code'),
      },
      {
        id: RESERVED_CRITERIA_CODE.LEVEL,
        valueId: String(watchedLevel),
      },
    ].filter((criterion) =>
      Boolean(criterion.valueId),
    ) as Array<CustomerCriterion>,
  };
};

export const useCustomerFormStepper = ({
  onSubmit,
  customerLevel,
}: {
  onSubmit: () => void;
  customerLevel: number;
}) => {
  const [activeStep, setActiveStep] = React.useState(
    CustomerUtils.GENERAL_FORM_STEP,
  );
  const CustomerFormPrivileges = useCustomerFormPrivileges({
    level: customerLevel,
  });
  const handlers = React.useMemo(
    () => ({
      set: (target: number) => setActiveStep(target),
      onNext: () => {
        if (
          activeStep <
          CustomerUtils.getAuthorizedSteps(CustomerFormPrivileges).length - 1
        ) {
          setActiveStep((prev) => prev + 1);
        } else {
          onSubmit();
        }
      },
      onPrevious: () => {
        setActiveStep((prev) => prev - 1);
      },
    }),
    [activeStep, onSubmit, CustomerFormPrivileges],
  );

  return [activeStep, handlers] as const;
};

export const useCustomerFormLevel = ({
  parent,
}: {
  parent: ICustomerGeneralForm['parent'];
}) => {
  const manageCustomers = useManageCustomers({
    getOneById: {
      args: { id: parent?.id?.value as string },
      enabled: Boolean(parent?.id?.value),
    },
  });
  const entityParent = manageCustomers.getOneById.data?.data;

  if (!parent?.id) {
    return CUSTOMER_LEVEL.ROOT;
  }
  if (parent.id && !entityParent) {
    return -1; // -1 means level not fetched yet
  }
  return Number(entityParent?.level) + 1;
};

export const useCustomerFormParentCustomer = ({
  methods,
}: {
  methods: ReturnType<typeof useCustomerForm>['forms'][0]['methods'];
}) => {
  const watchedParentId = useWatch({
    name: 'parent.id',
    control: methods.control,
  });
  const manageCustomers = useManageCustomers({
    getOneById: {
      args: { id: String(watchedParentId?.value) },
      enabled: Boolean(watchedParentId?.value),
      gcTime: 1000 * 60 * 2, // 2mn
    },
  });

  return manageCustomers.getOneById;
};

interface CustomerFormFields {
  GENERAL_FORM:
    | 'GROUP_NAME'
    | 'LOGO'
    | 'LABEL_FRONT_CHILDREN'
    | 'REDISTRIBUTION_MAX_DELAY'
    | 'DEFAULT_GENERAL_FIELDS'
    | 'LOCALE'
    | 'CUSTOMER_CODE';
  DELIVERY_FORM:
    | 'ECOTAX_ZONE'
    | 'VAT_ZONE'
    | 'ADDRESS'
    | 'ADDITIONAL_ADDRESS'
    | 'ZIP_CODE'
    | 'CITY'
    | 'COUNTRY'
    | 'BILLING_CODE';
  CONFIGURATION_FORM:
    | 'BUYER_ID'
    | 'ORDERING_ID'
    | 'CONSIGNEE_ID'
    | 'DMS_REFERENCE'
    | 'AUTO_VALIDATION'
    | 'ELIGIBLE_TO_CONTAINER_ORDERS';
}

export type CustomerFormStepKey =
  | 'CONTACT_FORM'
  | 'SUPPLIERS_FORM'
  | 'DELIVERY_FORM'
  | 'CONFIGURATION_FORM'
  | 'INTERNAL_STOCK_FORM'
  | 'CRITERIA_FORM'
  | 'GENERAL_FORM'
  | 'SETTING_TEMPLATE_FORM'
  | 'BRANDS_FORM'
  | 'STATUS_FORM'
  | 'QUOTE_BRANDS_FORM'
  | 'REDISTRIBUTION_BRANDS_FORM'
  | 'QUOTE_EXPRESS_FORM';

export type CustomerFormPrivileges = {
  [Key in CustomerFormStepKey]: Key extends keyof CustomerFormFields
    ? {
        visible: boolean;
        writable: boolean;
        fields: {
          [FieldKey in CustomerFormFields[Key]]: {
            visible: boolean;
            writable: boolean;
          };
        };
      }
    : {
        visible: boolean;
        writable: boolean;
      };
};

export const useCustomerFormPrivileges = ({
  level,
  customer,
}: {
  level: number;
  customer?: ICustomer;
}): CustomerFormPrivileges => {
  const acl = useAccessControl();
  const { auth, application } = useApplicationContext();
  const { isEnabledFeatures } = useAccessControl();
  const isRedistributionEnabled = isEnabledFeatures([FEATURES.REDISTRIBUTION]);
  const isQuoteEnabled = isEnabledFeatures([FEATURES.QUOTE]);
  // if the customerId IS NOT in the attached entities AND the user can see/edit it, we can assume it's a child of an attached customers
  // AND if there is NO customerId => we're actually creating customer and it will for sure be a child for one of my attached customers
  const isChild = customer
    ? !application.entities.map((customer) => customer.id).includes(customer.id)
    : true;
  const inMyAttachedEntities = customer
    ? application.entities.map((customer) => customer.id).includes(customer.id)
    : true;

  const isEditing = Boolean(customer?.id);
  const isN1 = level === CUSTOMER_LEVEL.N1;
  const isN2plus = level > CUSTOMER_LEVEL.N1;

  return {
    GENERAL_FORM: {
      visible: acl.isGranted(
        [
          ROLES.ROLE_CUSTOMER_CREATE,
          ROLES.ROLE_CUSTOMER_UPDATE,
          ROLES.ROLE_CUSTOMER_GET,
        ],
        'affirmative',
      ),
      writable:
        acl.isGranted(
          [ROLES.ROLE_CUSTOMER_CREATE, ROLES.ROLE_CUSTOMER_UPDATE],
          'affirmative',
        ) ||
        auth.isWyzUser ||
        isChild,
      fields: {
        LABEL_FRONT_CHILDREN: {
          visible: Boolean(customer?.hasChildren),
          writable: true,
        },
        LOGO: {
          visible: true,
          writable:
            inMyAttachedEntities ||
            auth.isWyzUser ||
            acl.isGranted(
              [
                ROLES.ROLE_CUSTOMER_LOGO_CREATE,
                ROLES.ROLE_CUSTOMER_LOGO_UPDATE,
              ],
              'unanimous',
            ),
        },
        GROUP_NAME: {
          visible:
            isN1 &&
            acl.isGranted(
              [
                ROLES.ROLE_CUSTOMER_CUSTOMER_GROUP_CREATE,
                ROLES.ROLE_CUSTOMER_CUSTOMER_GROUP_UPDATE,
              ],
              'unanimous',
            ),
          writable: auth.isWyzUser,
        },
        REDISTRIBUTION_MAX_DELAY: {
          visible: isN1 && (auth.isWyzUser || isChild),
          writable: isN1 && (auth.isWyzUser || isChild),
        },
        LOCALE: {
          visible: auth.isWyzUser || isChild,
          writable: auth.isWyzUser || isChild,
        },
        // Create a special field for special cases
        DEFAULT_GENERAL_FIELDS: {
          visible: auth.isWyzUser || isChild,
          writable: auth.isWyzUser || isChild,
        },
        CUSTOMER_CODE: {
          visible: true,
          writable: auth.isWyzUser,
        },
      },
    },
    CRITERIA_FORM: {
      visible: acl.isGranted(
        [
          ROLES.ROLE_CUSTOMER_CREATE,
          ROLES.ROLE_CUSTOMER_UPDATE,
          ROLES.ROLE_CUSTOMER_GET,
        ],
        'affirmative',
      ),
      writable: auth.isWyzUser || isChild,
    },
    QUOTE_EXPRESS_FORM: {
      writable:
        acl.isEnabledFeatures([FEATURES.QUOTE_EXPRESS]) &&
        acl.isGranted([ROLES.ROLE_CUSTOMER_QUOTE_FORM], 'affirmative'),
      visible:
        acl.isEnabledFeatures([FEATURES.QUOTE_EXPRESS]) &&
        acl.isGranted([ROLES.ROLE_CUSTOMER_QUOTE_FORM], 'affirmative'),
    },
    CONTACT_FORM: {
      visible: acl.isGranted(
        [
          ROLES.ROLE_CUSTOMER_CREATE,
          ROLES.ROLE_CUSTOMER_UPDATE,
          ROLES.ROLE_CUSTOMER_GET,
        ],
        'affirmative',
      ),
      writable: true,
    },
    DELIVERY_FORM: {
      visible: acl.isGranted(
        [
          ROLES.ROLE_CUSTOMER_CREATE,
          ROLES.ROLE_CUSTOMER_UPDATE,
          ROLES.ROLE_CUSTOMER_GET,
        ],
        'affirmative',
      ),
      writable: auth.isWyzUser || isChild,
      fields: {
        VAT_ZONE: {
          visible:
            acl.isGranted(
              [
                ROLES.ROLE_CUSTOMER_VAT_ZONE_CREATE,
                ROLES.ROLE_CUSTOMER_VAT_ZONE_UPDATE,
              ],
              'unanimous',
            ) && isN1,
          writable: auth.isWyzUser || isChild,
        },
        BILLING_CODE: {
          visible: acl.isGranted(
            [
              ROLES.ROLE_CUSTOMER_BILLING_CODE_CREATE,
              ROLES.ROLE_CUSTOMER_BILLING_CODE_UPDATE,
            ],
            'unanimous',
          ),
          writable: auth.isWyzUser || isChild,
        },
        ADDITIONAL_ADDRESS: {
          visible: true,
          writable: auth.isWyzUser || isChild,
        },
        ADDRESS: { visible: true, writable: auth.isWyzUser || isChild },
        CITY: { visible: true, writable: auth.isWyzUser || isChild },
        COUNTRY: { visible: true, writable: auth.isWyzUser || isChild },
        ECOTAX_ZONE: { visible: true, writable: auth.isWyzUser || isChild },
        ZIP_CODE: { visible: true, writable: auth.isWyzUser || isChild },
      },
    },
    CONFIGURATION_FORM: {
      visible:
        auth.isWyzUser ||
        (isN1 &&
          acl.isGranted(
            [
              ROLES.ROLE_CUSTOMER_CONTAINER_ELIGIBILITY_CREATE,
              ROLES.ROLE_CUSTOMER_CONTAINER_ELIGIBILITY_UPDATE,
            ],
            'affirmative',
          )) ||
        (isN2plus &&
          acl.isGranted(
            [
              ROLES.ROLE_CUSTOMER_AUTO_VALIDATION_UPDATE,
              ROLES.ROLE_CUSTOMER_DMS_REF_UPDATE,
              ROLES.ROLE_CUSTOMER_CONTAINER_ELIGIBILITY_CREATE,
              ROLES.ROLE_CUSTOMER_CONTAINER_ELIGIBILITY_UPDATE,
            ],
            'affirmative',
          )),
      writable:
        auth.isWyzUser ||
        (isChild &&
          acl.isGranted([
            ROLES.ROLE_CUSTOMER_DMS_REF_UPDATE,
            ROLES.ROLE_CUSTOMER_AUTO_VALIDATION_UPDATE,
          ])) ||
        acl.isGranted(
          [
            ROLES.ROLE_CUSTOMER_EDI_WHEEL_CREATE,
            ROLES.ROLE_CUSTOMER_EDI_WHEEL_UPDATE,
            ROLES.ROLE_CUSTOMER_CONTAINER_ELIGIBILITY_CREATE,
            ROLES.ROLE_CUSTOMER_CONTAINER_ELIGIBILITY_UPDATE,
          ],
          'affirmative',
        ),
      fields: {
        BUYER_ID: {
          visible:
            auth.isWyzUser ||
            acl.isGranted([
              ROLES.ROLE_CUSTOMER_EDI_WHEEL_CREATE,
              ROLES.ROLE_CUSTOMER_EDI_WHEEL_UPDATE,
            ]),
          writable: true,
        },
        ORDERING_ID: {
          visible:
            auth.isWyzUser ||
            acl.isGranted([
              ROLES.ROLE_CUSTOMER_EDI_WHEEL_CREATE,
              ROLES.ROLE_CUSTOMER_EDI_WHEEL_UPDATE,
            ]),
          writable: true,
        },
        CONSIGNEE_ID: {
          visible:
            auth.isWyzUser ||
            acl.isGranted([
              ROLES.ROLE_CUSTOMER_EDI_WHEEL_CREATE,
              ROLES.ROLE_CUSTOMER_EDI_WHEEL_UPDATE,
            ]),
          writable: true,
        },
        DMS_REFERENCE: {
          visible:
            isN2plus &&
            (auth.isWyzUser ||
              acl.isGranted([ROLES.ROLE_CUSTOMER_DMS_REF_UPDATE])),
          writable: isChild,
        },
        AUTO_VALIDATION: {
          visible:
            isN2plus &&
            (auth.isWyzUser ||
              acl.isGranted([ROLES.ROLE_CUSTOMER_AUTO_VALIDATION_UPDATE])),
          writable: isChild,
        },
        ELIGIBLE_TO_CONTAINER_ORDERS: {
          visible:
            isN1 &&
            (auth.isWyzUser ||
              acl.isGranted(
                [
                  ROLES.ROLE_CUSTOMER_CONTAINER_ELIGIBILITY_CREATE,
                  ROLES.ROLE_CUSTOMER_CONTAINER_ELIGIBILITY_UPDATE,
                ],
                'affirmative',
              )),
          writable:
            isN2plus &&
            (auth.isWyzUser ||
              acl.isGranted(
                [
                  ROLES.ROLE_CUSTOMER_CONTAINER_ELIGIBILITY_CREATE,
                  ROLES.ROLE_CUSTOMER_CONTAINER_ELIGIBILITY_UPDATE,
                ],
                'affirmative',
              )),
        },
      },
    },
    SUPPLIERS_FORM: {
      visible:
        isN1 &&
        acl.isGranted(
          [
            ROLES.ROLE_CUSTOMER_SUPPLIER_TEMPLATE_CREATE,
            ROLES.ROLE_CUSTOMER_SUPPLIER_TEMPLATE_UPDATE,
          ],
          'affirmative',
        ),
      writable: auth.isWyzUser,
    },
    BRANDS_FORM: {
      visible:
        isN1 &&
        acl.isGranted(
          [
            ROLES.ROLE_CUSTOMER_BRAND_TEMPLATE_CREATE,
            ROLES.ROLE_CUSTOMER_BRAND_TEMPLATE_UPDATE,
          ],
          'affirmative',
        ),
      writable: auth.isWyzUser || isChild,
    },
    QUOTE_BRANDS_FORM: {
      visible:
        isQuoteEnabled &&
        acl.isGranted(
          [
            ROLES.ROLE_QUOTE_BRAND_TEMPLATE_CREATE,
            ROLES.ROLE_QUOTE_BRAND_TEMPLATE_UPDATE,
          ],
          'affirmative',
        ),
      writable: auth.isWyzUser || isChild,
    },
    REDISTRIBUTION_BRANDS_FORM: {
      visible:
        isN2plus &&
        isRedistributionEnabled &&
        acl.isGranted(
          [
            ROLES.ROLE_REDISTRIBUTION_BRAND_TEMPLATE_CREATE,
            ROLES.ROLE_REDISTRIBUTION_BRAND_TEMPLATE_UPDATE,
          ],
          'affirmative',
        ),
      writable: auth.isWyzUser || isChild,
    },
    SETTING_TEMPLATE_FORM: {
      visible:
        isN1 &&
        acl.isGranted(
          [
            ROLES.ROLE_CUSTOMER_SETTING_TEMPLATE_CREATE,
            ROLES.ROLE_CUSTOMER_SETTING_TEMPLATE_UPDATE,
          ],
          'affirmative',
        ),
      writable: auth.isWyzUser || isChild,
    },
    INTERNAL_STOCK_FORM: {
      visible:
        isEditing &&
        auth.isWyzUser &&
        acl.isGranted(
          [
            ROLES.ROLE_CUSTOMER_INTERNAL_STOCK_CREATE,
            ROLES.ROLE_CUSTOMER_INTERNAL_STOCK_UPDATE,
          ],
          'affirmative',
        ),
      writable: isEditing && auth.isWyzUser,
    },
    STATUS_FORM: {
      visible: auth.isWyzUser || isChild,
      writable: auth.isWyzUser || isChild,
    },
  };
};
